import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Animated,
  BackHandler,
  Easing,
  Image,
  Linking,
  Platform,
  PermissionsAndroid,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import Geolocation from '@react-native-community/geolocation';
import MapView, { Region } from 'react-native-maps';
import { MAP_PROVIDER } from '../config/maps';
import Button from '../components/ui/Button';
import Icon from '../components/ui/Icon';
import Text from '../components/ui/Text';
import {
  AddressLabel,
  addressLabels,
  addressName,
  formatAddress,
  Place,
  SavedAddress,
  placeFromPin,
  placeFromResult,
} from '../data/places';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import {
  fetchCities,
  isServiceable,
  ResolvedPlace,
  reverseGeocode,
  searchPlaces,
  ServiceCity,
} from '../services/location';
import {
  colors,
  hairline,
  inputText,
  radii,
  MAX_CONTENT_WIDTH,
  screenPadding,
  shadows,
  spacing,
  useResponsive,
} from '../theme';

/**
 * Picking where the work happens, from permission through to a saved address.
 *
 * One screen with a stage machine rather than six pushed screens: every stage
 * carries the same address forward, "back" has to unwind to the previous stage
 * rather than out of the flow, and there is no navigator in the project yet.
 *
 * ---------------------------------------------------------------------------
 * ALL FOUR PLATFORM CALLS ARE REAL
 * ---------------------------------------------------------------------------
 *   permission   PermissionsAndroid
 *   the fix      @react-native-community/geolocation, precise then coarse
 *   pin -> name  `reverseGeocode`, debounced on every drag
 *   name -> pin  `searchPlaces`, debounced on every keystroke
 *
 * The last one was the hold-out: search filtered a hardcoded list of London
 * places, so a manually typed address had no coordinates and was saved against
 * whatever pin the map happened to be showing. Both geocodes now go through
 * our own backend, which holds the upstream credential and rate limit.
 */

/**
 * How long to wait on the GPS radio before trying the coarse provider.
 *
 * Long enough for a real satellite fix outdoors from cold - that genuinely
 * takes ten seconds or more - and short enough that someone indoors, where it
 * will never arrive, is not left watching a radar sweep for half a minute.
 */
const PRECISE_FIX_MS = 12000;

/**
 * And how long to wait on the fused provider after that.
 *
 * Much shorter. A cell-tower or Wi-Fi fix does not need to acquire anything;
 * it either has a recent position to hand or it does not.
 */
const COARSE_FIX_MS = 6000;

/**
 * How far the region may sit from where this screen last put the pin before
 * that counts as the customer having moved it, in degrees.
 *
 * ~11 metres at the equator, and deliberately loose: `animateToRegion` settles
 * a hair off the coordinates it was handed, so an exact comparison would read
 * the screen's own animation as a drag and refuse every refinement.
 */
const PIN_MOVED_DEG = 0.0001;

/**
 * How long the pin must sit still before its address is looked up.
 *
 * A drag across a city fires `onRegionChangeComplete` several times on the
 * way, and the backend serialises geocodes at one per 1.1s - so without this,
 * one drag queues a chain of lookups for positions the pin has already left,
 * and the answer for where you actually stopped arrives last.
 *
 * 450ms is long enough to swallow a flick and short enough that a deliberate
 * placement feels answered rather than delayed.
 */
const RESOLVE_DEBOUNCE_MS = 450;

/**
 * How long typing has to stop before the search is sent.
 *
 * Longer than the pin's debounce, because the trigger is different in kind. A
 * drag settles when a finger lifts - one clear event - whereas typing "Baker
 * Street" is twelve of them, and firing on each would spend twelve upstream
 * lookups to answer the one query the customer meant.
 *
 * 300ms sits under the threshold where a list feels like it is lagging behind
 * the keyboard, and above a fast typist's gap between keystrokes.
 */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Below this many characters, nothing is sent.
 *
 * "a" matches half the country. The geocoder would work hard to return a page
 * of noise, and the customer would read that noise as the app being wrong
 * about what they typed rather than as them not having typed enough yet.
 */
const MIN_QUERY_LENGTH = 3;

/**
 * Above this many metres of reported error, the pin is called approximate.
 *
 * 150m is about the point where a fix stops identifying a building and starts
 * identifying a neighbourhood. A GPS fix outdoors reports 5–20; the fused
 * provider indoors regularly reports several hundred to a few thousand, and
 * that is the case worth warning about - the panel looks equally confident
 * either way, and someone who is not told will save a pin a suburb from their
 * front door.
 */
const ROUGH_FIX_METRES = 150;

/**
 * Where the map opens before a fix arrives.
 *
 * ---------------------------------------------------------------------------
 * WHY THESE COORDINATES ARE NEVER SHOWN AS AN ADDRESS
 * ---------------------------------------------------------------------------
 * This used to be Baker Street, to agree with a hard-coded seed address that
 * the flow fell back to when geolocation failed. That fallback is gone: a
 * failed fix now goes to the `unavailable` stage, which asks rather than
 * guesses. See the locate effect below.
 *
 * A `MapView` still needs *some* region to mount with, and this is only ever
 * on screen in the `found` stage - which is reachable only once a real fix or
 * a searched result has replaced it. It is a placeholder for the camera, not
 * a claim about where anybody is.
 */
const INITIAL_REGION: Region = {
  latitude: 20.5937,
  longitude: 78.9629,
  // Roughly a few streets across. Wide enough to place yourself, tight enough
  // that dragging picks a doorway rather than a district.
  latitudeDelta: 0.006,
  longitudeDelta: 0.006,
};

/**
 * `save` is gone: choosing the point and naming it are one step, done on the
 * map page.
 */
type Stage =
  | 'permission'
  | 'fetching'
  | 'found'
  | 'search'
  | 'saved'
  | 'unavailable';

/**
 * A platform geolocation failure, in words the customer can act on.
 *
 * The codes are fixed by the web geolocation spec that RN's module follows,
 * and each one has a DIFFERENT remedy: 1 is granted in Settings, 2 is the
 * location toggle in the pulldown, 3 is worth simply trying again. Telling
 * somebody the wrong one sends them somewhere that cannot help.
 */
const explainFix = (error?: { code?: number; message?: string }): string => {
  switch (error?.code) {
    case 1:
      return 'Location permission is off. Turn it on in Settings > Apps > Homingo > Permissions.';
    case 2:
      return "Couldn't get a location - check that location services are on.";
    case 3:
      return "Couldn't get a location in time. Try again, or move near a window.";
    default:
      return "Couldn't get your location. Try again, or search for your address.";
  }
};

type Props = {
  /** Handed the finished address; the caller decides where to go next. */
  onDone: (address: SavedAddress) => void;
  /** Skip the whole flow - the app still works without an address. */
  onSkip: () => void;
  /**
   * Where the flow opens. `permission` for the first run; `fetching` for
   * someone adding a second address, who has already answered that question
   * and is only here for the map.
   */
  /**
   * Where the flow opens.
   *
   *   permission  first run - the app has never asked
   *   fetching    straight to a GPS fix, for someone who already answered
   *   search      straight to typing, with GPS offered as a control
   *
   * `search` is what "Add new address" uses. Someone adding a SECOND address
   * is usually not standing at it - it is a parent's flat, an office, the
   * place they are having something delivered - so opening on a fix locates
   * the wrong building and makes them undo it. Typing is the shorter path
   * there, and the GPS button is one tap away for when it is not.
   */
  /*
   * `found` is in the list because the tests open on the map directly - the
   * recentre button, the back button and the save claim all live there, and
   * reaching that stage through a mocked GPS fix would test the mock. The
   * screen has always accepted it; only this type disagreed, and a test that
   * has to cast is a test that stops reading like the call it stands for.
   */
  initialStage?: Extract<Stage, 'permission' | 'fetching' | 'search' | 'found'>;
  /**
   * An existing address to open ON, for editing rather than adding.
   *
   * Editing used to hand this screen nothing at all: "Edit" simply opened the
   * blank add-flow, so the map started over the middle of India, the label
   * chips reset to Home and the note was gone - and coming back from the
   * search tab left the panel empty, because there was never anything in it.
   *
   * Given one, the flow opens at `found` with the pin, the label, the note and
   * the default flag already set, which is what makes it an edit. `onDone`
   * still reports a whole `SavedAddress`, so the caller updates rather than
   * inserts by matching on `place.id`.
   */
  initialAddress?: SavedAddress;
  /**
   * Whether this address can actually be kept anywhere.
   *
   * ---------------------------------------------------------------------------
   * IT USED TO SAY "ADDRESS SAVED!" TO PEOPLE WITH NO ACCOUNT
   * ---------------------------------------------------------------------------
   * The address book lives on the platform, so there is nowhere to put an
   * address until somebody has signed in. On the first run there is no account
   * yet - and this screen still asked for a label, a contact name and a
   * landmark, then showed a full-screen tick reading "Address Saved!" and
   * "saved as Home". None of it was kept: `App` takes the line and the pin, for
   * coverage and quoting, and drops the rest. The customer then found an empty
   * address book and was asked for the address again at checkout.
   *
   * Defaults to TRUE because every other caller is behind a sign-in gate - see
   * `gated` in `HomeScreen` - and does save. The first run is the one place
   * that passes false.
   *
   * False changes two things and nothing else: the fields that only describe a
   * saved row are not asked for, and the flow ends on the map rather than on a
   * claim that is not true.
   */
  canSave?: boolean;
};

// ------------------------------------------------------------- small parts

function PlaceRow({
  place,
  onPress,
  showChevron = true,
}: {
  place: Place;
  onPress: () => void;
  showChevron?: boolean;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${place.title}, ${place.subtitle}`}
      onPress={onPress}
      style={({ pressed }) => [styles.row, pressed ? styles.pressed : null]}
    >
      <Icon name="pin" size={19} color={colors.inkMuted} />
      <View style={styles.rowText}>
        <Text variant="subtitle" numberOfLines={1}>
          {place.title}
        </Text>
        <Text variant="caption" numberOfLines={1}>
          {place.subtitle}
        </Text>
      </View>
      {showChevron ? (
        <Icon name="chevronRight" size={16} color={colors.borderStrong} />
      ) : null}
    </Pressable>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <View style={styles.field}>
      <Text variant="caption" style={styles.fieldLabel}>
        {label}
      </Text>
      {children}
    </View>
  );
}

// ------------------------------------------------------------------ screen

function LocationScreen({
  onDone,
  onSkip,
  initialStage = 'permission',
  initialAddress,
  canSave = true,
}: Props) {
  const { width, height } = useWindowDimensions();
  const resp = useResponsive();

  /**
   * The radar, and the ring pulsing out of it.
   *
   * A share of the width, so it is the same optical size on every phone -
   * capped, because on a tablet 72% is a 700pt circle on a screen whose copy
   * sits underneath it, and the wait stops reading as a detail of the page and
   * starts being the page.
   */
  const radarSize = Math.min(Math.round(width * 0.72), 320);
  const insets = useSafeAreaInsets();
  /* What the bottom panel has to clear. See `useKeyboardHeight` for why this
     is read from the keyboard rather than left to `KeyboardAvoidingView`. */
  const keyboardHeight = useKeyboardHeight();

  /* An address to edit opens on the map with it, whatever `initialStage`
     says - the caller asks for a stage for the ADD flow, and editing is not
     that flow. */
  const [stage, setStage] = useState<Stage>(
    initialAddress ? 'found' : initialStage,
  );

  /**
   * Whether this whole flow is an edit rather than an add.
   *
   * Read from the prop and never changed: a screen opened on an existing
   * address stays an edit for as long as it is up, even after the pin has been
   * dragged somewhere else entirely. Drives the two things that differ -
   * whether the name follows the pin (`pinFollows`) and what the search field
   * opens with.
   */
  const editing = !!initialAddress;
  /*
   * Starts empty, not at a seed address.
   *
   * `detectedPlace` (221B Baker Street) used to be the initial value, so the
   * panel had something to render before the fix landed. Nothing renders it
   * before `found` now, and `found` is only reachable once a real position or
   * a searched result has set it.
   */
  const [place, setPlace] = useState<Place | null>(
    initialAddress?.place ?? null,
  );
  /** Why the fix failed, in words, for the `unavailable` stage. */
  const [failure, setFailure] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [label, setLabel] = useState<AddressLabel>(
    initialAddress?.label ?? 'Home',
  );
  /** Free text, only asked for once "Other" is chosen. */
  const [customLabel, setCustomLabel] = useState(
    initialAddress?.customLabel ?? '',
  );
  const [note, setNote] = useState(initialAddress?.note ?? '');
  /** Who answers the door - asked with the address, not at checkout. */
  const [contactName, setContactName] = useState(
    initialAddress?.contactName ?? '',
  );
  const [isDefault, setIsDefault] = useState(
    initialAddress?.isDefault ?? true,
  );

  /**
   * What the map is currently centred on.
   *
   * Held here rather than read off the map so the value survives a stage
   * change - the map unmounts when the flow moves to save, and the coordinates
   * have to outlive it.
   */
  const [region, setRegion] = useState<Region>(
    initialAddress?.coords
      ? {
          ...INITIAL_REGION,
          latitude: initialAddress.coords.lat,
          longitude: initialAddress.coords.lng,
        }
      : INITIAL_REGION,
  );

  /**
   * How much of the map the details sheet covers, measured rather than assumed.
   *
   * Two things need it and both have to agree: the pin, which must sit in the
   * middle of the *visible* map rather than the middle of the screen, and the
   * map's own padding, which is what makes the reported centre the visible
   * centre. A guessed constant would put the pin and the coordinates it reports
   * in different places.
   */
  const [sheetHeight, setSheetHeight] = useState(0);

  /**
   * Whether the native map exists yet.
   *
   * `mapPadding` is forwarded straight to `GoogleMap.setPadding`, and the view
   * is mounted before that object is created - so setting it on the first
   * render crashes on a null reference. Gating the prop on `onMapReady` means
   * it is never sent to a map that is not there.
   *
   * ---------------------------------------------------------------------------
   * IT MUST BE RESET, AND FORGETTING THAT IS THE BUG THIS ONCE HAD
   * ---------------------------------------------------------------------------
   * `MapView` lives inside the `found` branch, so every trip to search or to
   * the saved screen unmounts it and coming back mounts a NEW one. The flag is
   * plain component state and survives all of that - so on the second visit it
   * was still `true`, `mapPadding` went out on the very first render, and it
   * reached `setPadding` before the new `GoogleMap` had been created:
   *
   *   Attempt to invoke virtual method
   *   'void com.google.android.gms.maps.GoogleMap.setPadding(int,int,int,int)'
   *   on a null object reference
   *
   * The first visit always worked, which is what made it look like a map or a
   * key problem rather than a stale flag. The effect below is the whole fix:
   * leaving `found` puts the gate back where a fresh map expects to find it.
   */
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    if (stage !== 'found') {
      setMapReady(false);
    }
  }, [stage]);

  /**
   * The map is uncontrolled - `initialRegion` plus this ref, not `region`.
   *
   * Feeding `region` back in as a prop makes the map fight the finger: every
   * pan sets state, the re-render pushes the old value back down, and the map
   * stutters. Animating through the ref moves it only when we mean to.
   */
  /**
   * Where a searched result put the pin, if the current place came from search.
   *
   * Null for a detected or saved place - only a search result has a position
   * of its own to have been dragged away FROM. Cleared as soon as one is no
   * longer what is being shown.
   */
  const [resultAt, setResultAt] = useState<{ lat: number; lng: number } | null>(
    null,
  );

  const mapRef = useRef<MapView>(null);

  /*
   * The last point this screen put the pin on ITSELF, and the region as it
   * stands right now.
   *
   * Together they answer one question the fix effect has to ask before it is
   * allowed to move anything: has the customer taken the map over? There is no
   * "user panned" event to read - `onRegionChangeComplete` fires for a
   * programmatic `animateToRegion` exactly as it does for a finger - so the
   * signal is the comparison. If the region still sits where this screen last
   * put it, nobody has dragged it.
   *
   * Refs rather than state on purpose: the fix callbacks are closures created
   * once per effect run, and reading `region` from one of them would read the
   * value as it was when the effect started, which is precisely the stale
   * answer that would let a late fix overwrite a deliberate drag.
   */
  const appliedAt = useRef<{ lat: number; lng: number } | null>(null);
  const regionRef = useRef(region);
  /* Written on every render rather than in an effect: a late fix can land
     between a render and an effect flush, and the comparison above has to be
     against what is on screen at that instant. */
  regionRef.current = region;

  /** Drives the radar pulse while locating. */
  const pulse = useRef(new Animated.Value(0)).current;

  /** Cross-fades one stage into the next so nothing snaps. */
  const enter = useRef(new Animated.Value(1)).current;

  const show = useCallback(
    (next: Stage) => {
      enter.setValue(0);
      setStage(next);
      Animated.timing(enter, {
        toValue: 1,
        duration: 260,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start();
    },
    [enter],
  );

  /*
   * Back steps to the previous stage instead of leaving.
   *
   * The stages are one screen driven by a flag, so Android has nothing to pop
   * and a press on "save" closed the app four steps into the flow.
   *
   * `fetching` and `saved` are not listed: both are transient states that
   * move on by themselves, and stepping back into one would restart work that
   * is already in flight. Backing out of either returns to where it started.
   * From `permission` there is nothing behind us, so back leaves as normal.
   */
  /*
   * Entering at `fetching` means the permission stage was skipped, so it is
   * not behind us - backing out of the map has to leave the flow entirely,
   * or it would land on a question already answered.
   */
  const previous = useMemo<Partial<Record<Stage, Stage>>>(
    () =>
      initialStage === 'permission'
        ? {
            fetching: 'permission',
            found: 'permission',
            /* Back from the failure goes to the ask, which is the one thing
               that might still fix it. */
            unavailable: 'permission',
            search: 'found',
            saved: 'found',
          }
        : initialStage === 'search'
        ? /* Opened straight into typing: `search` is the first stage, so it
             has nothing behind it and back must leave the flow. Only `saved`
             steps back, and to the map it was reached from. */
          { saved: 'found' }
        : { search: 'found', saved: 'found' },
    [initialStage],
  );

  /**
   * One step back - for Android's hardware button and the on-screen one alike.
   *
   * ---------------------------------------------------------------------------
   * IPHONES HAVE NO BACK BUTTON
   * ---------------------------------------------------------------------------
   * This lived only inside a `BackHandler` listener, which is Android's
   * hardware key. The map step drew no back control of its own, so on iOS the
   * only ways off it were saving an address or killing the app. The same
   * function now answers both, so the two platforms cannot drift apart.
   *
   * Returns whether back was handled here: false on a first run's front door,
   * where Android lets the key leave the app.
   */
  const goBack = useCallback((): boolean => {
    const back = previous[stage];
    if (back) {
      show(back);
      return true;
    }
    // Nothing behind us inside the flow. On a first run that is the app's
    // front door and back should leave; on a later visit it is Home.
    if (initialStage === 'permission') {
      return false;
    }
    onSkip();
    return true;
  }, [initialStage, onSkip, previous, show, stage]);

  /** Whether the on-screen back has anywhere to go. */
  const canGoBack = !!previous[stage] || initialStage !== 'permission';

  useEffect(() => {
    const sub = BackHandler.addEventListener('hardwareBackPress', goBack);
    return () => sub.remove();
  }, [goBack]);

  // Radar rings, only while the lookup is running.
  useEffect(() => {
    if (stage !== 'fetching') {
      return;
    }
    const loop = Animated.loop(
      Animated.timing(pulse, {
        toValue: 1,
        duration: 1600,
        easing: Easing.out(Easing.quad),
        useNativeDriver: true,
      }),
    );
    loop.start();
    return () => loop.stop();
  }, [pulse, stage]);

  /**
   * The real permission dialog.
   *
   * Android only. iOS has no request call - the prompt fires the first time a
   * position is read, using the reason string in Info.plist.
   *
   * A refusal is not a dead end: the flow still moves to `fetching`, which
   * falls back to the seed address, and the search field is the way on. The
   * app is usable without ever granting location.
   */
  /**
   * Location permission, asked for wherever a fix is about to be taken.
   *
   * ---------------------------------------------------------------------
   * WHY THIS IS SEPARATE FROM `requestPermission` BELOW
   * ---------------------------------------------------------------------
   * `requestPermission` belongs to the `permission` STAGE - it asks, then
   * moves the flow on to `fetching` whatever the answer was. That is right for
   * onboarding and wrong everywhere else: the recentre button and the "Use
   * current location" row both take a fix from the `found` stage, long after
   * that stage is behind the customer, and neither wants to advance anything.
   *
   * They also need the ANSWER, which `requestPermission` throws away. Without
   * it the recentre button called straight into `getCurrentPosition` on a
   * phone that had never granted location: the platform refused instantly,
   * both passes failed, and the only trace was a `console.warn` nobody sees.
   * The button spun and the pin stayed put, which is the bug this fixes.
   *
   * Returns true when a fix is worth attempting. On iOS that is always -
   * `requestAuthorization` is fire-and-forget and the read itself raises the
   * prompt - so the attempt goes ahead and its own failure path handles a
   * refusal.
   */
  const ensurePermission = useCallback(async (): Promise<boolean> => {
    if (Platform.OS !== 'android') {
      try {
        Geolocation.requestAuthorization();
      } catch {
        // Older iOS builds without the call. The read prompts anyway.
      }
      return true;
    }

    try {
      /* Asking again when it is already granted shows no dialog, but it does
         cost a round trip on every tap - and `check` is the honest question. */
      const already = await PermissionsAndroid.check(
        PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
      );
      if (already) {
        return true;
      }

      const result = await PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
      );
      return result === PermissionsAndroid.RESULTS.GRANTED;
    } catch {
      /* The dialog could not be shown. Let the fix attempt try and fail on
         its own terms rather than blocking it on a broken permission call. */
      return true;
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (Platform.OS === 'android') {
      const result = await PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
      );

      /*
       * The answer is logged rather than thrown away.
       *
       * The flow deliberately carries on whatever it is - see above - but
       * "denied" and "never_ask_again" are the single most common reason the
       * panel ends up showing the seed address, and without this there is
       * nothing anywhere to say so. `never_ask_again` in particular cannot be
       * recovered from in the app at all: the dialog will not appear again and
       * the only way back is Settings.
       */
      if (result !== PermissionsAndroid.RESULTS.GRANTED) {
        console.warn(
          `[location] permission ${result} - no fix will be possible. ` +
            (result === PermissionsAndroid.RESULTS.NEVER_ASK_AGAIN
              ? 'Grant it in Settings > Apps > Homingo > Permissions.'
              : 'The seed address will be used instead.'),
        );
      }
    }
    show('fetching');
  }, [show]);

  /**
   * A real position fix, in two attempts.
   *
   * ---------------------------------------------------------------------
   * WHY TWICE, AND WHY THE SECOND ONE MATTERS MORE
   * ---------------------------------------------------------------------
   * The first attempt asks for `enableHighAccuracy`, which on Android means
   * the GPS radio. Indoors, GPS frequently never fixes at all - the satellites
   * are simply not visible through a roof - and the call does not fail fast,
   * it burns the whole timeout and then reports one. On a desk in an office,
   * that is the normal case rather than the unlucky one.
   *
   * So a refusal is retried WITHOUT high accuracy, which lets the platform use
   * the fused provider: cell towers and known Wi-Fi networks. It is accurate to
   * a block rather than a doorstep, which is plenty for dropping a pin the
   * customer is about to drag anyway - and it works indoors, which the precise
   * fix does not.
   *
   * Only when both fail does the seed address appear. That fallback is why a
   * broken fix is so easy to miss: nothing errors, the flow simply carries on
   * showing "221B Baker Street, London" as though it had detected it. Both
   * failures are logged for exactly that reason.
   *
   * The address for whatever comes back is resolved separately - see the
   * reverse-geocode effect below.
   */
  useEffect(() => {
    if (stage !== 'fetching') {
      return;
    }

    let cancelled = false;

    /*
     * Permission is raised HERE now, not only by whatever navigated in.
     *
     * This effect used to read a position straight away and could not raise
     * the dialog itself, so every caller had to remember to `await
     * ensurePermission()` first - and the two that forgot (the recentre button
     * and, before it was fixed, the "use current location" row) failed
     * silently into `unavailable`. Asking here makes `fetching` safe to enter
     * from anywhere, which is what lets the cart's address sheet open straight
     * onto the map.
     *
     * A refusal is not handled specially: the read below fails and lands on
     * `unavailable`, which is exactly what a refusal should look like and what
     * it already did.
     */
    const settle = (next: Place) => {
      if (cancelled) {
        return;
      }
      setPlace(next);
      /* A fresh fix is not a search result. */
      setResultAt(null);
      show('found');
    };

    const onFix = (position: {
      coords: { latitude: number; longitude: number; accuracy?: number };
    }) => {
      const { latitude, longitude, accuracy } = position.coords;
      if (cancelled) {
        return;
      }
      /*
       * How far out the fix might be, in metres, straight from the platform.
       *
       * Kept because the two attempts produce wildly different numbers - a GPS
       * fix is accurate to a few metres, a cell-tower one to a kilometre or
       * more - and the panel is otherwise identical either way. Someone shown
       * a confident-looking "Current location" over a pin a suburb away has no
       * reason to think anything is wrong.
       */
      setAccuracy(typeof accuracy === 'number' ? accuracy : null);
      setRegion(current => ({ ...current, latitude, longitude }));
      /* Remembered so a later, better fix can tell "still where I put it" from
         "the customer has dragged this somewhere". See `appliedAt`. */
      appliedAt.current = { lat: latitude, lng: longitude };
      mapRef.current?.animateToRegion(
        { ...INITIAL_REGION, latitude, longitude },
        600,
      );
      settle({
        id: 'detected',
        title: 'Current location',
        // Coordinates until the reverse geocode lands. See `subtitle` below.
        subtitle: `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`,
      });
    };

    /** What went wrong, in words rather than a code nobody remembers. */
    const explain = (error: { code?: number; message?: string }): string => {
      switch (error?.code) {
        case 1:
          return 'permission denied';
        case 2:
          return 'position unavailable - location services may be off';
        case 3:
          return 'timed out';
        default:
          return error?.message ?? 'unknown error';
      }
    };

    /* See the note at the top of this effect: the dialog is raised here so
       `fetching` is safe to enter from anywhere, rather than every caller
       having to remember to ask first. */
    ensurePermission().then(() => {
      if (cancelled) {
        return;
      }

      /*
       * BOTH AT ONCE, AND THE FIRST ONE ON SCREEN WINS
       * =====================================================================
       * These used to run one after the other: precise first, and the coarse
       * one only after the precise had FAILED. That reads as careful and costs
       * twelve seconds of blank screen for nothing, because of how the failure
       * arrives - a GPS attempt indoors does not fail fast, it burns the whole
       * of `PRECISE_FIX_MS` and only then reports a timeout.
       *
       * Measured on the phone this was reported from:
       *
       *   gps provider     last location = null   (four days of uptime)
       *   fused provider   hAcc  20m
       *   network provider hAcc 100m, one minute old
       *
       * GPS had never produced a fix at all, so the twelve seconds were spent
       * waiting for something that was never coming - and then the coarse pass
       * answered from the fused provider anyway. The old order paid full price
       * for the exact answer it was going to get regardless.
       *
       * Running them together does not trade accuracy away, which was the
       * worry worth having. The precise attempt is unchanged and still gets its
       * full `PRECISE_FIX_MS`; it simply no longer has to fail before anything
       * is allowed on screen. `apply` below is what keeps it honest: a later
       * fix may only move the pin if it is genuinely better AND the customer
       * has not taken the map over in the meantime.
       *
       * `unavailable` now needs BOTH to have failed rather than the inner one,
       * which is why the two flags exist. Reporting the first failure would
       * send somebody to Settings while a perfectly good fix was still in
       * flight.
       */
      let onScreen: number | null = null;
      let coarseFailed = false;
      let preciseFailed = false;

      /**
       * How much better a later fix has to be before it is allowed to move a
       * pin the customer is already looking at, as a fraction of what is on
       * screen.
       *
       * A jump is a cost - somebody reading the address watches it change
       * under them - so it has to buy something. 100m replacing 120m is noise;
       * 8m replacing 100m is the difference between the right building and the
       * right street.
       */
      const WORTH_MOVING_FOR = 0.5;

      const apply = (position: {
        coords: { latitude: number; longitude: number; accuracy?: number };
      }) => {
        if (cancelled) {
          return;
        }
        const next = position.coords.accuracy ?? Number.POSITIVE_INFINITY;

        if (onScreen !== null) {
          /* Still where this screen put it? If not, the customer has dragged
             the pin and that is the more recent instruction - see `appliedAt`. */
          const put = appliedAt.current;
          const now = regionRef.current;
          const untouched =
            !!put &&
            Math.abs(now.latitude - put.lat) < PIN_MOVED_DEG &&
            Math.abs(now.longitude - put.lng) < PIN_MOVED_DEG;

          if (!untouched || !(next < onScreen * WORTH_MOVING_FOR)) {
            return;
          }
        }

        onScreen = next;
        onFix(position);
      };

      /** Neither provider answered - only then is there nothing to show. */
      const giveUp = (why: string) => {
        if (cancelled || onScreen !== null || !coarseFailed || !preciseFailed) {
          return;
        }
        /*
         * No fallback address. This used to settle on the Baker Street seed,
         * which meant a phone with GPS off carried on to the `found` stage
         * showing "221B Baker Street, London" under a confident "Current
         * location" label - a real address, in another country, that the
         * customer could save and book against.
         *
         * `explain` already turns the platform code into words, so the stage
         * below can say WHICH thing went wrong: permission is fixed in
         * Settings, services-off is fixed in the pulldown, and a timeout is
         * worth simply retrying. Telling someone the wrong one sends them to
         * the wrong place.
         */
        setFailure(why);
        show('unavailable');
      };

      /* The one that actually answers indoors, and the reason the screen now
         fills in well under a second. A last known position up to a minute old
         beats no position - and it is only ever the STARTING pin now, not the
         final word, because the precise attempt is already running beside it. */
      Geolocation.getCurrentPosition(
        apply,
        coarse => {
          console.warn(`[location] coarse fix failed: ${explain(coarse)}`);
          coarseFailed = true;
          giveUp(explain(coarse));
        },
        {
          enableHighAccuracy: false,
          timeout: COARSE_FIX_MS,
          maximumAge: 60000,
        },
      );

      Geolocation.getCurrentPosition(
        apply,
        precise => {
          console.warn(`[location] precise fix failed: ${explain(precise)}`);
          preciseFailed = true;
          giveUp(explain(precise));
        },
        {
          enableHighAccuracy: true,
          timeout: PRECISE_FIX_MS,
          /*
           * No cached position on the precise attempt: this is the one whose
           * whole job is to be better than what is already showing.
           *
           * `maximumAge` is permission to answer from the last fix ANY app on
           * the device happened to take - and if a browser or a weather widget
           * took a coarse network fix eight seconds ago, that is what comes
           * back, instantly, with `enableHighAccuracy` never engaging the GPS
           * at all. Zero forces a fresh one.
           *
           * That mattered more when this was the only attempt that could reach
           * the screen. It still matters: a cached coarse answer arriving here
           * would clear `preciseFailed` without improving anything, and the
           * real GPS fix behind it would never be asked for.
           */
          maximumAge: 0,
        },
      );
    });

    return () => {
      cancelled = true;
    };
  }, [ensurePermission, show, stage]);

  /**
   * The address for wherever the pin is now, once the server has named it.
   *
   * Keyed on the coordinates it was resolved FOR, not just stored loose. Two
   * drags in quick succession can settle out of order, and a label pinned to
   * the wrong coordinates is worse than no label - it is a street name under a
   * pin that is somewhere else, which is exactly the kind of wrong nobody
   * checks.
   */
  /**
   * Whether the lookup for the pin's CURRENT position came back empty.
   *
   * Distinct from "not resolved yet": that is the ordinary state during the
   * debounce and while the request is in flight, and it must not be reported
   * as an error. This is set only when a request actually failed, and cleared
   * the moment the pin moves again.
   */
  const [resolveFailed, setResolveFailed] = useState(false);

  const [resolved, setResolved] = useState<{
    key: string;
    place: ResolvedPlace;
  } | null>(null);

  /** Radius the last fix could be out by, in metres. Null before one lands. */
  const [accuracy, setAccuracy] = useState<number | null>(null);

  /**
   * Whether a recentre is in flight, so the button can say so.
   *
   * A fix takes seconds and nothing else on this screen moves while it is
   * being acquired, so without this the button absorbs the tap and looks
   * broken - which is the same complaint that produced it.
   */
  const [locating, setLocating] = useState(false);

  /**
   * Why the last recentre attempt came back with nothing, in words.
   *
   * Null when nothing has failed. Cleared the moment another attempt starts
   * and the moment one succeeds, so a message can never outlive the state it
   * describes - a stale "location is off" over a pin that has just moved is
   * worse than saying nothing.
   */
  const [locateError, setLocateError] = useState<string | null>(null);

  /**
   * Put the pin back on the customer.
   *
   * The flow drops a pin on the fix once, at `fetching`, and from then on the
   * map is the customer's to move. There was no way back: pan away from your
   * street and the only route to it was killing the screen and re-entering the
   * flow, which is why this button exists.
   *
   * Two passes, precise then coarse, for the reason the locate effect explains
   * at length - indoors the GPS radio frequently never fixes, and the fused
   * provider is what actually answers. The timeouts and `maximumAge` are kept
   * identical to that effect so the button and the initial drop cannot
   * disagree about how hard they tried.
   *
   * `setPlace({ id: 'detected' })` is not cosmetic. `pinFollows` keys on that
   * id, and without it the panel would re-centre the map while carrying on
   * displaying whatever address was last chosen from search - the same stale
   * -name bug this screen has fought twice already.
   */
  const recentre = useCallback(async () => {
    setLocating(true);

    /*
     * Permission first, or the two passes below are theatre.
     *
     * Without this the button called straight into `getCurrentPosition` on a
     * phone that had never been asked. Android refuses immediately, both
     * passes fall through to the warn, and the customer sees a button that
     * spins briefly and does nothing - which is exactly what was reported.
     */
    const allowed = await ensurePermission();
    if (!allowed) {
      setLocating(false);
      setLocateError(
        'Location permission is off. Turn it on in Settings > Apps > ' +
          'Homingo > Permissions, or search for your address instead.',
      );
      return;
    }

    /* A previous refusal must not stay on screen over a fresh attempt. */
    setLocateError(null);

    const onFix = (position: {
      coords: { latitude: number; longitude: number; accuracy?: number };
    }) => {
      const { latitude, longitude, accuracy } = position.coords;
      setLocating(false);
      /* A fix landed, so whatever the last failure said is no longer true. */
      setLocateError(null);
      setAccuracy(typeof accuracy === 'number' ? accuracy : null);
      setRegion(current => ({ ...current, latitude, longitude }));
      /* Remembered so a later, better fix can tell "still where I put it" from
         "the customer has dragged this somewhere". See `appliedAt`. */
      appliedAt.current = { lat: latitude, lng: longitude };
      mapRef.current?.animateToRegion(
        { ...INITIAL_REGION, latitude, longitude },
        600,
      );
      /* Hands the name back to the pin - see `pinFollows`. */
      setResultAt(null);
      setPlace({
        id: 'detected',
        title: 'Current location',
        subtitle: `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`,
      });
    };

    Geolocation.getCurrentPosition(
      onFix,
      () => {
        Geolocation.getCurrentPosition(
          onFix,
          coarse => {
            /* Left where they are, deliberately. The pin is already somewhere
               deliberate, and yanking it to a stale fix would be worse than
               declining to move it. */
            console.warn(
              `[location] recentre failed: ${coarse?.message ?? 'unknown'}`,
            );
            setLocating(false);
            /*
             * Said out loud, not just logged.
             *
             * The warn above is for us; the customer got nothing at all and
             * had no way to tell a failed fix from a button that does not
             * work. `explainFix` turns the platform's code into the one
             * sentence that names the actual remedy - services off and a
             * timeout are fixed in different places.
             */
            setLocateError(explainFix(coarse));
          },
          {
            enableHighAccuracy: false,
            timeout: COARSE_FIX_MS,
            maximumAge: 60000,
          },
        );
      },
      { enableHighAccuracy: true, timeout: PRECISE_FIX_MS, maximumAge: 0 },
    );
  }, [ensurePermission]);


  /**
   * The cities the platform actually operates in.
   *
   * Null until the list lands. Null is NOT "nowhere is covered" - it is "we do
   * not know yet", and the two have to stay apart: treating an unfetched list
   * as empty would tell every customer they are out of area for the second
   * before it arrives, and permanently if the request fails.
   */
  const [cities, setCities] = useState<ServiceCity[] | null>(null);

  /**
   * What the search box has come back with.
   *
   * `status` rather than a pair of booleans, because the list has four states
   * that must not be confused for one another - and the pair that matters most
   * is `error` against `empty`. An empty list drawn on a failed request tells
   * the customer their street does not exist, when what happened is that we
   * never managed to ask.
   *
   * `for` is the query the results belong to, and it is what stops a slow
   * answer landing under a newer question: the response for "bak" arriving
   * after the customer has typed "baker street" is still results for "bak",
   * and showing them would be a list that disagrees with the box above it.
   */
  const [search, setSearch] = useState<{
    for: string;
    status: 'idle' | 'loading' | 'ready' | 'error';
    results: Place[];
  }>({ for: '', status: 'idle', results: [] });

  /**
   * Whether the pin is worth doubting.
   *
   * Only meaningful for a detected place - a pin the customer dragged there
   * themselves, or picked from search, is exactly where they meant it to be
   * however bad the original fix was.
   */
  const roughFix =
    place?.id === 'detected' && accuracy !== null && accuracy > ROUGH_FIX_METRES;

  /* Four decimals, ~11m - the same rounding the backend caches on, so a drag
     too small to change the answer does not ask for it again. */
  const pinKey = `${region.latitude.toFixed(4)},${region.longitude.toFixed(4)}`;

  /**
   * Whether the name on screen follows the pin.
   *
   * True for a freshly detected fix, and true for an address opened to be
   * EDITED - in both cases the point is that the customer is moving the pin,
   * so the address under it has to keep up.
   *
   * This used to be a bare `place?.id === 'detected'` test in three places.
   * That is what made dragging the pin do nothing while editing: a saved
   * address carries its server row id, never `'detected'`, so the geocode
   * effect returned early, the subtitle kept rendering the old stored text and
   * the panel calmly showed the previous street over a pin somewhere else
   * entirely.
   *
   * A place picked from SEARCH is excluded on purpose. It arrives with a real
   * address of its own and the map is moved to match it, so re-deriving its
   * name from the pin would only ever replace a precise result with whatever
   * the geocoder makes of those coordinates.
   */
  /**
   * How far the pin may sit from a searched result before it stops being that
   * result, in degrees. Roughly 30 metres at Indian latitudes.
   *
   * Not zero: `animateToRegion` settles a pixel or two off the coordinate it
   * was given and `onRegionChangeComplete` reports that back, so an exact
   * comparison would call every search result "moved" the instant it landed.
   * Small enough that a deliberate drag down the street is well outside it.
   */
  const RESULT_RADIUS_DEG = 0.0003;

  /**
   * Whether the pin has been dragged off the search result it was placed on.
   *
   * This is the fix for choosing a city with "Change" and then moving the pin:
   * the result kept its name no matter where the pin went, so picking Mumbai
   * and then dragging to Indore saved MUMBAI - the words from the search over
   * coordinates in another state. The pin is the more recent instruction, so
   * once it leaves the result, the result stops being the answer.
   */
  const movedOffResult =
    !!resultAt &&
    (Math.abs(region.latitude - resultAt.lat) > RESULT_RADIUS_DEG ||
      Math.abs(region.longitude - resultAt.lng) > RESULT_RADIUS_DEG);

  const pinFollows =
    place?.id === 'detected' || editing || movedOffResult;

  /**
   * Naming the pin.
   *
   * Debounced rather than fired on every `onRegionChangeComplete`: a drag
   * across a city settles several times on the way, and each settle would be
   * an upstream geocode. The backend serialises those at one per 1.1s, so an
   * undebounced drag would queue a chain of lookups for positions the pin has
   * already left.
   *
   * Failures are swallowed on purpose. A point with no address, a dead
   * network, an unreachable upstream - all of them just leave the coordinates
   * on screen, which is what this panel showed before any of this existed. The
   * address is a nicety; the coordinates are what the booking travels on.
   */
  useEffect(() => {
    if (stage !== 'found' || !pinFollows) {
      return;
    }
    if (resolved?.key === pinKey) {
      return;
    }

    setResolveFailed(false);

    const controller = new AbortController();
    const timer = setTimeout(() => {
      reverseGeocode(region.latitude, region.longitude, controller.signal)
        .then(next => setResolved({ key: pinKey, place: next }))
        .catch(() => {
          /*
           * Recorded rather than merely swallowed.
           *
           * The coordinates still stay on screen - that part was always right.
           * What was missing is that nothing downstream knew the lookup had
           * failed, so "Save Address" happily wrote "Pinned location" plus a
           * lat/lng into the address book as though it were a street. A row
           * like that cannot be read by a professional and cannot be searched
           * for; it is a failure stored as data.
           *
           * `AbortError` is not a failure: superseding a request is this
           * effect's own cleanup doing its job, and flagging it would light up
           * the warning mid-drag on every pin move.
           */
          if (!controller.signal.aborted) {
            setResolveFailed(true);
          }
        });
    }, RESOLVE_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [pinFollows, pinKey, region.latitude, region.longitude, resolved?.key, stage]);

  /*
   * A detected place has no street name of its own - only coordinates - so its
   * subtitle is whatever the pin currently resolves to, and falls back to the
   * coordinates until it does. Dragging the pin therefore changes what the
   * panel says, which is what makes the drag mean anything.
   *
   * The `pinKey` comparison is what stops a stale answer showing under a moved
   * pin: the moment the region changes, the old label stops matching and the
   * coordinates come back until the new one lands.
   *
   * A place chosen from search already has a real address and keeps it.
   */
  /**
   * Running the search.
   *
   * Debounced and abortable for the same reason the reverse geocode is, but
   * with one extra job: the request that is still in flight when the next
   * keystroke lands is CANCELLED, not merely ignored. Left running, a slow
   * answer for "bak" could resolve after the answer for "baker street" and
   * overwrite it - the classic out-of-order autocomplete, where the list
   * settles on results for a prefix of what is in the box.
   *
   * Only runs on the search stage. Typing, leaving, and having the answer
   * arrive into a screen that has moved on is a wasted upstream call against a
   * rate limit that is shared with every other customer.
   */
  useEffect(() => {
    if (stage !== 'search') {
      return;
    }

    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      /* Not an empty result set - a prompt. The list draws the difference. */
      setSearch({ for: trimmed, status: 'idle', results: [] });
      return;
    }

    setSearch({ for: trimmed, status: 'loading', results: [] });

    let stale = false;
    const controller = new AbortController();

    const timer = setTimeout(() => {
      searchPlaces(trimmed, controller.signal)
        .then(found => {
          if (stale) {
            return;
          }
          setSearch({
            for: trimmed,
            status: 'ready',
            results: found.map(placeFromResult),
          });
        })
        .catch(() => {
          /* A supersession is not a failure - the newer run sets the state
             itself. Reporting here would flash an error between keystrokes. */
          if (stale) {
            return;
          }
          setSearch({ for: trimmed, status: 'error', results: [] });
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      stale = true;
      clearTimeout(timer);
      controller.abort();
    };
    /*
     * `query` and `stage` ONLY - deliberately not `search`.
     *
     * ---------------------------------------------------------------------
     * WHY THE SEARCH STATE MUST NOT BE A DEPENDENCY
     * ---------------------------------------------------------------------
     * This effect WRITES `search`. Depending on it too meant every write
     * re-ran the effect, and every re-run fired the cleanup - which aborted
     * the request the previous run had just scheduled. Guarding the re-run
     * ("already asked, do not ask again") did not help: the guard returned
     * early, so the run that was supposed to send the request was cancelled
     * by the run that refused to replace it. Nothing was ever sent, and the
     * panel sat on "Searching…" against an endpoint that answers in 400ms.
     *
     * Keyed on the query alone, one keystroke means exactly one run: set
     * loading, debounce, send, settle. A re-render caused by the result
     * cannot restart it, because the result is not something it watches.
     */
     
  }, [query, stage]);

  /**
   * Fetching where the platform operates, once.
   *
   * Not per pin: the list changes when the business enters a new city, which
   * is not something that happens while somebody is dragging a map. Failure is
   * swallowed and leaves `cities` null, which reads as "unknown" everywhere
   * below - the flow stays usable and the booking is checked server-side
   * regardless. Better to let someone save an address we cannot serve than to
   * block someone we CAN serve because a list did not load.
   */
  useEffect(() => {
    const controller = new AbortController();

    fetchCities(controller.signal)
      .then(found => setCities(found))
      .catch(() => {
        /* Stays null. See above. */
      });

    return () => controller.abort();
  }, []);

  /**
   * Whether this pin is somewhere we can send a professional.
   *
   * Three states, not two. `null` means the answer is not known - the city
   * list has not landed, or the pin has not been resolved yet - and the panel
   * must say nothing at all rather than guess, because both wrong guesses are
   * bad: "not available" over a covered address loses a customer who was
   * ready to book, and silence over an uncovered one is the failure at
   * checkout this whole check exists to move earlier.
   */
  const covered = useMemo<boolean | null>(() => {
    if (!cities) {
      return null;
    }

    /* A searched place resolves its own city; a dragged pin uses whatever the
       reverse geocode last returned for THIS position. */
    const candidates =
      resolved?.key === pinKey ? resolved.place.cityCandidates : undefined;

    if (!candidates?.length) {
      return null;
    }

    return isServiceable(candidates, cities);
  }, [cities, pinKey, resolved]);

  /**
   * Taking a searched address as the chosen one.
   *
   * The map has to move with it. A result carries its own coordinates, and
   * without this the pin stayed wherever it was - so the address that got
   * SAVED was the old pin under the new name, which is a booking sent to the
   * wrong street with the right words on it.
   *
   * `setRegion` as well as `animateToRegion` because the two serve different
   * things: the animation is what the customer sees, `region` is what the
   * address is actually built from further down, and the map is unmounted
   * right now so it cannot report anything back.
   */
  const chooseResult = useCallback((chosen: Place) => {
    setPlace(chosen);
    /* Remembered so the pin can tell whether it is still standing on this
       result or has since been dragged off it - see `movedOffResult`. */
    setResultAt(chosen.coords ?? null);
    if (chosen.coords) {
      const next = {
        ...INITIAL_REGION,
        latitude: chosen.coords.lat,
        longitude: chosen.coords.lng,
      };
      setRegion(next);
      mapRef.current?.animateToRegion(next, 600);
    }
    show('found');
  }, [show]);

  /*
   * The resolved name for where the pin currently is, or null until it lands.
   *
   * Both lines below are built from this, so they can never disagree about
   * which position they are describing.
   */
  const atPin = resolved?.key === pinKey ? resolved.place : null;

  /**
   * The pin's address as ONE derivation, both lines from the same call.
   *
   * `title` used to come from `placeFromPin` while `subtitle` was set to the
   * raw `atPin.label`, and that is what printed the address twice: the label
   * is the geocoder's whole line - "Paramount Apartments, 109, Telephone
   * Nagar, Indore..." - whose first segment IS the title, so the panel showed
   * the street in black and then the street again, with its tail, in green.
   *
   * `placeFromPin` already solves this. It builds the second line from the
   * area, city and postcode with any segment equal to the title dropped, and
   * only falls back to the full label when that leaves nothing.
   */
  const atPinPlace =
    pinFollows && atPin
      ? placeFromPin(atPin, {
          latitude: region.latitude,
          longitude: region.longitude,
        })
      : null;

  const title = pinFollows
    ? atPinPlace?.title ?? 'Pinned location'
    : place?.title ?? '';

  const subtitle = pinFollows
    ? atPinPlace?.subtitle ??
      `${region.latitude.toFixed(5)}, ${region.longitude.toFixed(5)}`
    : place?.subtitle ?? '';

  /**
   * What "Change" puts in the search box.
   *
   * The full line rather than just the title, because the geocoder searches
   * better with the area and city attached - "MG Road" alone matches one in
   * every city in the country, while "MG Road, Vijay Nagar, Indore" comes back
   * with the neighbours of the address actually being edited.
   *
   * Never the coordinates: a pin that has not resolved yet has "22.71, 75.85"
   * as its subtitle, and putting that in a text search returns nothing at all.
   * An unresolved pin seeds the title alone, or nothing.
   */
  /**
   * Whether there is a real address to save, as opposed to a bare pin.
   *
   * A pin that never resolved has "Pinned location" over its coordinates, and
   * saving one produces exactly the rows this screen was showing: an address
   * book full of "Pinned location, 22.72396, 75.90488" that nobody - customer
   * or professional - can act on. Blocking the save is what keeps those out.
   *
   * Only applies while the name is following the pin. A place picked from
   * search always has a real address, and a saved one being re-labelled
   * without moving the pin keeps whatever it already had.
   */
  const hasRealAddress = !pinFollows || !!atPin;

  const searchSeed = (() => {
    const unresolved = pinFollows && !atPin;
    if (unresolved) {
      return '';
    }
    const line = [title, subtitle].filter(Boolean).join(', ');
    /* `subtitle` often already begins with the title - the geocoder's own
       full label - so a naive join would repeat it. */
    return subtitle.startsWith(title) ? subtitle : line;
  })();

  const fade = { opacity: enter };

  // ------------------------------------------------------------- rendering

  if (stage === 'permission') {
    return (
      <View style={styles.dark}>
        {/*
          Explicit width and height, not just `absoluteFill`: an `Image` given
          only insets can fall back to its intrinsic size, so the artwork would
          land anchored top-left at 863x1823 instead of covering the screen.
        */}
        <Image
          source={require('../../assets/uploads/location/bg.png')}
          style={[StyleSheet.absoluteFill, { width, height }]}
          resizeMode="cover"
        />
        <SafeAreaView style={styles.darkBody} edges={['top', 'bottom']}>
          {/*
            The lockup, which the artwork no longer carries.

            `bg.png` used to have the wordmark baked into it, and the comment
            here said so as the reason this screen drew none of its own. The
            new artwork is the line drawing alone, so without this the flow
            opened on an unbranded screen.

            `logo_transparent.png` rather than `assets/logo/logo .png`: the
            latter is the PRO app's lockup - a different product - and it
            carries a solid pale background that would sit as a light slab on
            top of the drawing. This one is the customer wordmark with a real
            alpha channel, so the artwork shows through around it.

            Sits below the top edge rather than against it: the drawing's own
            sky occupies that band, and a mark pinned to the very top reads as
            a header bar rather than as part of the composition.
          */}
          <Animated.View style={[styles.brandWrap, fade]}>
            <Image
              source={require('../../assets/uploads/logo/logo_transparent.png')}
              style={styles.brand}
              resizeMode="contain"
              accessibilityRole="image"
              accessibilityLabel="Homingo"
            />
          </Animated.View>

          <Animated.View style={[styles.permissionCard, fade]}>
            <View style={styles.permissionMark}>
              <Icon name="pin" size={26} color={colors.primary} />
            </View>
            {/*
              Guideline 5.1.1(iv), and the shape of this card is the rule.

              A pre-permission screen may EXPLAIN why the app wants location.
              It may not campaign for a yes, and it may not offer a way past
              itself: Apple rejected 1.0 (3) for both. So the heading states
              what the permission buys rather than asking for it, the button
              says "Continue" rather than "Allow Location", and there is no
              second action - pressing Continue always reaches the system
              prompt, which is the only place the answer is given.

              A refusal is not a dead end. `requestPermission` advances to
              `fetching` whatever the answer, the fix fails, and the
              `unavailable` stage below offers Settings and manual entry. That
              path is what replaces the old "Not Now".
            */}
            <Text variant="titleLg" align="center">
              Find pros near you
            </Text>
            <Text
              variant="bodySm"
              align="center"
              color={colors.inkMuted}
              style={styles.permissionCopy}
            >
              We use your location to find the best pros near you and give an
              accurate arrival time.
            </Text>

            <Button
              label="Continue"
              onPress={requestPermission}
              style={styles.permissionCta}
            />
          </Animated.View>
        </SafeAreaView>
      </View>
    );
  }

  /*
   * Back, for the two stages that are all message and no map.
   *
   * Both had none. Adding an address from Home opens straight on `fetching`,
   * so on an iPhone the only ways off it were waiting out the GPS timeouts or
   * closing the app - and `unavailable` offered retries and a detour through
   * search, never simply "not now". The same `goBack` as the map's disc and
   * Android's key, so all three agree on where back goes.
   *
   * Positioned off the inset by hand rather than laid out in the flow: both
   * stages centre or pin their content against the full height, and a row
   * above it would shift the whole composition down. Absolute children are
   * measured from the outer edge, so the inset is added here.
   */
  const stageBack = canGoBack ? (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel="Back"
      onPress={goBack}
      hitSlop={8}
      style={({ pressed }) => [
        styles.floatBack,
        styles.stageBack,
        { top: insets.top + spacing.sm },
        pressed && styles.pressed,
      ]}
    >
      <Icon name="chevronLeft" size={22} color={colors.ink} />
    </Pressable>
  ) : null;

  if (stage === 'fetching') {
    return (
      <SafeAreaView style={styles.root} edges={['top', 'bottom']}>
        <Animated.View style={[styles.centre, fade]}>
          <View style={styles.radarWrap}>
            <Image
              source={require('../../assets/uploads/location/radar.png')}
              style={[styles.radar, { width: radarSize, height: radarSize }]}
              resizeMode="contain"
            />
            {/* A ring expanding out of the mark, so the wait reads as active. */}
            <Animated.View
              style={[
                styles.ping,
                {
                  width: radarSize,
                  height: radarSize,
                  borderRadius: radarSize / 2,
                  opacity: pulse.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0.45, 0],
                  }),
                  transform: [
                    {
                      scale: pulse.interpolate({
                        inputRange: [0, 1],
                        outputRange: [0.45, 1],
                      }),
                    },
                  ],
                },
              ]}
              pointerEvents="none"
            />
          </View>

          <Text variant="titleLg" align="center" style={styles.fetchTitle}>
            Fetching your location
          </Text>
          <Text variant="bodySm" align="center" color={colors.inkMuted}>
            This won’t take long.
          </Text>
        </Animated.View>

        {stageBack}
      </SafeAreaView>
    );
  }

  if (stage === 'unavailable') {
    /*
     * Which of the three failures this was decides where the customer is sent,
     * so the copy and the secondary action both branch on it. `explain` in the
     * locate effect produces these strings.
     */
    const denied = failure?.includes('permission') ?? false;
    const servicesOff = failure?.includes('services may be off') ?? false;

    return (
      <View style={styles.dark}>
        <Image
          source={require('../../assets/uploads/location/bg.png')}
          style={[StyleSheet.absoluteFill, { width, height }]}
          resizeMode="cover"
        />
        <SafeAreaView style={styles.darkBody} edges={['top', 'bottom']}>
          {/* The same lockup as the permission stage - both draw `bg.png`,
              and a screen that loses the mark on the failure path reads as a
              different app at the worst moment. */}
          <Animated.View style={[styles.brandWrap, fade]}>
            <Image
              source={require('../../assets/uploads/logo/logo_transparent.png')}
              style={styles.brand}
              resizeMode="contain"
              accessibilityRole="image"
              accessibilityLabel="Homingo"
            />
          </Animated.View>

          <Animated.View style={[styles.permissionCard, fade]}>
            <View style={styles.permissionMark}>
              <Icon name="pin" size={26} color={colors.primary} />
            </View>
            <Text variant="titleLg" align="center">
              {denied
                ? 'Location permission is off'
                : servicesOff
                ? 'Turn on location'
                : 'Couldn’t find your location'}
            </Text>
            <Text
              variant="bodySm"
              align="center"
              color={colors.inkMuted}
              style={styles.permissionCopy}
            >
              {denied
                ? 'Homingo needs location access to find pros near you. Turn it on in Settings, then try again.'
                : servicesOff
                ? 'Your device’s location services are switched off. Turn them on, then try again.'
                : 'We couldn’t get a signal - this is common indoors. Move near a window and try again, or search for your address instead.'}
            </Text>

            <Button
              label="Try again"
              onPress={() => {
                setFailure(null);
                show('fetching');
              }}
              style={styles.permissionCta}
            />
            {denied ? (
              <Pressable
                accessibilityRole="button"
                /*
                 * The only way back from `never_ask_again` - the system dialog
                 * will not appear a second time, so retrying alone can never
                 * succeed for this customer.
                 */
                onPress={() => Linking.openSettings()}
                hitSlop={10}
                style={({ pressed }) => [
                  styles.textButton,
                  pressed ? styles.pressed : null,
                ]}
              >
                <Text variant="label" color={colors.inkMuted}>
                  Open Settings
                </Text>
              </Pressable>
            ) : null}
            <Pressable
              accessibilityRole="button"
              onPress={() => show('search')}
              hitSlop={10}
              style={({ pressed }) => [
                styles.textButton,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text variant="label" color={colors.inkMuted}>
                Enter address manually
              </Text>
            </Pressable>
          </Animated.View>
        </SafeAreaView>

        {stageBack}
      </View>
    );
  }

  /**
   * The address this flow has arrived at, built from wherever the pin now is.
   *
   * One builder for both endings: the map step uses it when there is nothing
   * to save and finishes there, and the confirmation step uses it after a
   * save. Two copies of this object would be two chances for the words and the
   * coordinates to disagree.
   *
   * Null only when no place has been resolved yet, which the stage machine
   * already prevents - see the note in the saved branch.
   */
  const buildAddress = (): SavedAddress | null =>
    place
      ? {
          /*
           * The resolved street, not the coordinates and not "Current
           * location". See `detectedAddress` - it is where the pin stops being
           * a status and becomes an address. Everything downstream (the header,
           * the cart, the booking, the professional's job sheet) reads it.
           *
           * `pinFollows`, not `id === 'detected'`: an address being EDITED is
           * also built from wherever its pin now is. Keying off the id meant a
           * dragged pin saved the old street text against the new coordinates -
           * a professional sent to the right pin with the wrong words.
           */
          place: pinFollows ? placeFromPin(atPin, region, place.id) : place,
          label,
          customLabel: customLabel.trim() || undefined,
          note: note || undefined,
          contactName: contactName.trim() || undefined,
          isDefault,
          /*
           * Carried out of the flow because the server needs them: the address
           * book stores a pin and resolves a city from it. Without these an
           * address can be shown in the app and cannot be booked against.
           *
           * `region` is right for both paths - the detected pin is what the map
           * is centred on, and `chooseResult` moved the map onto a searched
           * result's own coordinates before getting here.
           */
          coords: { lat: region.latitude, lng: region.longitude },
        }
      : null;

  if (stage === 'found') {
    return (
      <View style={styles.root}>
        {/*
          Full bleed, behind everything.

          The map was boxed in a rounded card with the details below it, which
          made the thing being chosen the smaller half of the screen. Now the
          map is the screen and the details ride over it.
        */}
        <MapView
          ref={mapRef}
          provider={MAP_PROVIDER}
          style={StyleSheet.absoluteFill}
          /*
            Where the fix put us, NOT the seed.
            ------------------------------------------------------------------
            This must be `region` and it is the whole reason the screen used to
            open in London whatever your phone reported.
            `MapView` only exists in this branch, so it mounts AFTER the fix has
            landed - which means the `animateToRegion` call in the locate effect
            runs against a null ref and does nothing at all. The map then opened
            at the hard-coded seed, settled there, and its own
            `onRegionChangeComplete` fired `setRegion(Baker Street)`, throwing
            away the real coordinates a moment after they arrived. The address
            panel was then perfectly correct about a place 7,000km away.
            Reading `region` is safe precisely because the map is uncontrolled:
            `initialRegion` is consulted once, at mount, and by then `region`
            already holds either the real fix or the fallback. Every move after
            that goes through the ref, so this never fights the finger.
          */
          initialRegion={region}
          onRegionChangeComplete={setRegion}
          showsMyLocationButton={false}
          toolbarEnabled={false}
          onMapReady={() => setMapReady(true)}
          /*
            Padding, not a smaller map: this tells Google the bottom strip is
            covered, so the coordinate it reports as the centre is the centre of
            what can actually be seen - the point the pin is drawn on.

            Withheld until the map is ready. Passed earlier it reaches
            `GoogleMap.setPadding` before there is a `GoogleMap`.
          */
          mapPadding={
            mapReady && sheetHeight > 0
              ? { top: 0, right: 0, bottom: sheetHeight, left: 0 }
              : undefined
          }
        />

        {/*
          The pin is a fixed overlay, not a map marker.

          Address pickers work by holding the pin still and sliding the map
          under it: whatever sits at the centre when the drag stops is the
          chosen point. A real marker would have to be dragged itself, and its
          gesture would fight the map's.

          It centres in the strip above the sheet, and `translateY` lifts it by
          half the glyph so the pin's point - not its middle - lands on the
          coordinate. `pointerEvents="none"` so it never swallows a pan.
        */}
        <View
          style={[styles.pinLayer, { bottom: sheetHeight }]}
          pointerEvents="none"
        >
          {/*
            A white disc under the glyph, and the glyph itself in white.

            `colors.primary` is green800 - very dark, and Google's default map
            style is dark green and grey, so the pin was a dark mark on dark
            ground competing with every road, park and label around it. On the
            satellite-ish tiles it disappeared outright.

            The disc is what carries the contrast: white reads against every
            tile Google draws, and the ring plus shadow lifts it off whatever
            is underneath. The glyph then goes white-on-green rather than
            green-on-map, so the thing being pointed with is the brightest
            object on screen - which is what a pin is for.
          */}
          <View style={styles.pinPuck}>
            <Icon name="pin" size={22} color={colors.onPrimary} />
          </View>
          {/* The point itself. The puck marks the position; this says which
              exact pixel it means. */}
          <View style={styles.pinStem} />
        </View>

        {/* Over the map, so it needs its own ground to stay legible. */}
        <SafeAreaView
          style={styles.floatHeader}
          edges={['top']}
          pointerEvents="box-none"
        >
          {/*
            Opens search.

            This was a plain `View` - a pill that says "Select Location", sits
            where a control sits, and did precisely nothing when tapped. It
            reads as the way to choose an address, so it is now the way to
            choose an address: same destination as "Change" on the panel.
          */}
          <View style={styles.floatRow} pointerEvents="box-none">
            {/*
              Back, on the map itself.

              There was none: leaving this step was Android's hardware key or
              nothing, so an iPhone could only save an address or be closed.
              Same disc as "back to my location" below, so the two read as one
              family of controls over the map.
            */}
            {canGoBack ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Back"
                onPress={goBack}
                hitSlop={8}
                style={({ pressed }) => [
                  styles.floatBack,
                  pressed && styles.pressed,
                ]}
              >
                <Icon name="chevronLeft" size={22} color={colors.ink} />
              </Pressable>
            ) : null}
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Search for a location"
              onPress={() => show('search')}
              style={({ pressed }) => [
                styles.floatTitle,
                styles.floatTitleInRow,
                pressed && styles.pressed,
              ]}
            >
              <Icon name="search" size={16} color={colors.primary} />
              <Text variant="labelSm">Select Location</Text>
            </Pressable>
          </View>
        </SafeAreaView>

        {/*
          Back to the customer.

          Sits directly above the sheet on the right, where every map app puts
          it. Before this the pin could be panned away from your own street
          with no route back short of leaving the flow and starting again.
        */}
        <View
          style={[styles.recentreLayer, { bottom: sheetHeight + spacing.lg }]}
          pointerEvents="box-none"
        >
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Move the pin to my location"
            accessibilityState={{ busy: locating }}
            onPress={recentre}
            disabled={locating}
            hitSlop={8}
            style={({ pressed }) => [
              styles.recentre,
              pressed && styles.pressed,
            ]}
          >
            <Icon
              name="target"
              size={22}
              color={locating ? colors.inkMuted : colors.primary}
            />
          </Pressable>

          {/*
            Why the last attempt failed, beside the button that failed.

            Here rather than in a toast because this is where the customer is
            looking - they just tapped the target and are watching the pin.
            `accessibilityLiveRegion` is what makes a screen reader announce
            it, since nothing about the layout changes focus.
          */}
          {locateError && !locating ? (
            <View
              style={styles.locateError}
              accessibilityLiveRegion="polite"
            >
              <Text variant="labelSm" color={colors.danger}>
                {locateError}
              </Text>
            </View>
          ) : null}
        </View>

        <Animated.View
          /*
             The panel is full-bleed on a phone and a centred column on a
             tablet - the same rule every sheet in the app follows. It carries
             a form, and a label at one edge of a 10-inch screen with its field
             at the other is a form nobody can follow.
          */
          style={[
            styles.sheet,
            /* Pulled in from both edges by the same amount, rather than
               centred with `alignSelf` - this box is absolutely positioned,
               and its insets are the only thing that decides where it sits.
               `sideInset` is 0 on a phone, so the panel stays full-bleed. */
            {
              left: resp.sideInset,
              right: resp.sideInset,
              borderTopLeftRadius: radii.xl,
              borderTopRightRadius: radii.xl,
              /*
               * Lifted clear of the keyboard by its exact height.
               *
               * This panel is absolutely positioned against the bottom of the
               * screen, so it only moves if something moves it. That used to
               * be `KeyboardAvoidingView` with `behavior="height"`, which
               * relies on the window shrinking under `adjustResize` - and from
               * Android 15 the window no longer shrinks, so the keys opened
               * straight over "Flat, floor or landmark" and the Save button.
               * See `useKeyboardHeight`.
               */
              bottom: keyboardHeight,
              /*
               * And clear of the system navigation bar.
               *
               * The panel is pinned to the WINDOW's bottom edge, which on an
               * edge-to-edge Android build runs underneath the three-button
               * bar - so "Save Address" was drawn behind it. The lift used to
               * be a `marginBottom` on the button alone and was landing as
               * zero here; carrying it on the panel instead means the white
               * surface extends under the bar and the button sits above it.
               *
               * The floor matters: a three-button bar is ~48dp and a phone
               * that reports no inset at all would otherwise clip the button
               * again. `Math.max` rather than a sum, so a device reporting a
               * real inset is not padded twice.
               */
              paddingBottom:
                spacing.lg +
                (keyboardHeight > 0 ? 0 : Math.max(insets.bottom, spacing.xxl)),
            },
            fade,
          ]}
          onLayout={event => setSheetHeight(event.nativeEvent.layout.height)}
        >
          <View style={styles.panelInner}>
            {/* Says the panel is a surface over the map, and that there is more
                of it below the fold. */}
            <View style={styles.grabber} />

            <ScrollView
              style={styles.panelScroll}
              contentContainerStyle={styles.panelBody}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
            >
              <View style={styles.foundRow}>
                <Icon name="pin" size={20} color={colors.primary} />
                <View style={styles.rowText}>
                  {/* Follows the pin while it is being moved - see
                      `pinFollows`. Showing the stored `place.title` here is
                      what left the OLD street name sitting above a freshly
                      dragged pin. */}
                  <Text variant="subtitle">{title}</Text>
                  <Text variant="caption">{subtitle}</Text>

                  {/*
                    Said out loud, not left to be discovered.

                    A fix good to a kilometre and a fix good to five metres
                    draw the identical panel, and the customer has no way to
                    tell which one they are looking at - until a professional
                    turns up in the wrong street. The number is included
                    because "approximate" alone does not say whether to nudge
                    the pin or move it across town.
                  */}
                  {roughFix ? (
                    <Text variant="caption" color={colors.danger}>
                      Approximate to about{' '}
                      {accuracy && accuracy >= 1000
                        ? `${(accuracy / 1000).toFixed(1)} km`
                        : `${Math.round(accuracy ?? 0)} m`}
                      . Drag the map to place the pin exactly.
                    </Text>
                  ) : null}

                  {/*
                    Out of area, said at the map rather than at checkout.

                    Only when `covered` is exactly false - null means the city
                    list or the geocode has not landed, and a maybe must not be
                    drawn as a no. Worded as "not yet" because that is the
                    truth: the platform is in two cities and adding more, so
                    this is a waiting list, not a rejection.

                    The address can still be saved. Someone who lives outside
                    the area today is exactly who should be able to keep it on
                    file for when we arrive, and blocking the save would also
                    block them from setting an address for a relative in a city
                    we DO cover.
                  */}
                  {covered === false ? (
                    <View style={styles.coverage}>
                      {/* `inkMuted`, not `danger`: nothing has gone wrong and
                          the address still saves. Red here would read as a
                          rejection of an address that is perfectly valid. */}
                      <Icon name="pin" size={13} color={colors.inkMuted} />
                      <Text
                        variant="caption"
                        color={colors.inkMuted}
                        style={styles.coverageText}
                      >
                        We are not in this area yet. We are coming soon. You
                        can still save this address.
                      </Text>
                    </View>
                  ) : null}
                </View>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Change address"
                  /*
                   * Opens the search WITH the current address in the box.
                   *
                   * This used to be a bare `show('search')`, which dropped
                   * someone into an empty field and an empty list - so
                   * "Change" threw away the very address they were changing
                   * and made them retype a street they were already looking
                   * at. Seeding the query means the field reads what the panel
                   * read, and the search effect (which keys off `query`) runs
                   * it immediately, so the related places for that address are
                   * on screen before a single key is pressed.
                   *
                   * `title` and not `place.title` - while the pin is being
                   * moved the title IS the resolved line, and that is the one
                   * on screen to carry across.
                   */
                  onPress={() => {
                    setQuery(searchSeed);
                    show('search');
                  }}
                  hitSlop={10}
                >
                  <Text variant="labelSm" color={colors.primary}>
                    Change
                  </Text>
                </Pressable>
              </View>

              {/* Everything from here to the default switch describes a row
                  in the address book. With no account there is no row, so
                  asking for any of it is asking for nothing - see `canSave`. */}
              {canSave ? (
                <>
              <Field label="Save as">
                <View style={styles.chips}>
                  {addressLabels.map(option => {
                    const active = option === label;
                    return (
                      <Pressable
                        key={option}
                        accessibilityRole="button"
                        accessibilityState={{ selected: active }}
                        onPress={() => setLabel(option)}
                        style={[styles.chip, active ? styles.chipActive : null]}
                      >
                        <Text
                          variant="labelSm"
                          color={active ? colors.onPrimary : colors.inkBody}
                        >
                          {option}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
                {/* Only under "Other". Home and Work already name themselves,
                    so showing this always would be a field to skip twice out of
                    three times. */}
                {label === 'Other' ? (
                  <TextInput
                    style={styles.nameBox}
                    value={customLabel}
                    onChangeText={setCustomLabel}
                    placeholder="Name this address"
                    placeholderTextColor={colors.inkSubtle}
                    accessibilityLabel="Name this address"
                    autoCapitalize="words"
                    returnKeyType="done"
                  />
                ) : null}
              </Field>

              {/* Who the professional asks for - the booker and the person at
                  the door are often not the same person. */}
              <Field label="Contact person at this address">
                <TextInput
                  style={styles.nameBox}
                  value={contactName}
                  onChangeText={setContactName}
                  placeholder="Name of who'll be home"
                  placeholderTextColor={colors.inkSubtle}
                  accessibilityLabel="Contact person at this address"
                  autoCapitalize="words"
                  returnKeyType="done"
                />
              </Field>

              <Field label="Flat, floor or landmark">
                <TextInput
                  style={styles.noteBox}
                  value={note}
                  onChangeText={setNote}
                  placeholder="Gate code, floor, landmark"
                  placeholderTextColor={colors.inkSubtle}
                  multiline
                />
              </Field>

              <View style={styles.defaultRow}>
                <View style={styles.rowText}>
                  <Text variant="subtitle">Set as default</Text>
                  <Text variant="caption">Used for new bookings.</Text>
                </View>
                <Switch
                  value={isDefault}
                  onValueChange={setIsDefault}
                  trackColor={{ true: colors.primary, false: colors.border }}
                  thumbColor={colors.surface}
                />
              </View>
                </>
              ) : null}
            </ScrollView>

            {/* Outside the scroll view, so it stays put while the fields move
                under it - the one control that must never be hunted for. */}
            {/*
              Said in the panel, next to the button it disables.

              `resolveFailed` is the lookup having actually come back empty;
              the plain not-yet state says nothing, because during the debounce
              there is nothing wrong to report.
            */}
            {pinFollows && !atPin ? (
              <Text
                variant="caption"
                color={resolveFailed ? colors.danger : colors.inkMuted}
                style={styles.resolveNote}
              >
                {resolveFailed
                  ? 'We could not read an address for this pin. Move it slightly, or search for the address instead.'
                  : 'Finding the address for this pin…'}
              </Text>
            ) : null}

            <Button
              label={canSave ? 'Save Address' : 'Use this location'}
              /*
               * Disabled until the pin has an address. This used to save
               * regardless, which is how "Pinned location, 22.72396, 75.90488"
               * ended up in the address book five times over - a row that
               * cannot be delivered to.
               */
              disabled={!hasRealAddress}
              /*
               * Straight out of the flow when there is nothing to save. The
               * tick screen exists to confirm a save happened; without one it
               * is only a claim, and the customer has already read the street
               * on this very panel.
               */
              onPress={() => {
                if (canSave) {
                  show('saved');
                  return;
                }
                const built = buildAddress();
                if (built) onDone(built);
              }}
              /* The system bar is cleared by the panel's own padding now -
                 see the note on `paddingBottom` above. A margin here as well
                 would leave a band of dead panel under the button. */
              style={styles.panelCta}
            />
          </View>
        </Animated.View>
      </View>
    );
  }

  if (stage === 'search') {
    return (
      <SafeAreaView style={styles.root} edges={['top', 'bottom']}>
        <View style={styles.header}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Back"
            /*
             * Back goes to the map only when there IS one to go back to.
             *
             * `found` renders the resolved line straight out of `place`, so
             * arriving there with `place` still null draws the panel with an
             * empty title and no address - which is exactly what backing out
             * of search used to do on a flow that had not resolved a pin yet.
             * With nothing picked, the honest destination is the screen this
             * search was opened from.
             */
            onPress={() => (place ? show('found') : onSkip())}
            hitSlop={12}
          >
            <Icon name="chevronLeft" size={22} color={colors.ink} />
          </Pressable>
          <Text variant="title">Select Location</Text>
          <View style={styles.headerSpacer} />
        </View>

        <View style={styles.searchField}>
          <Icon name="search" size={18} color={colors.inkMuted} />
          <TextInput
            style={styles.searchInput}
            value={query}
            onChangeText={setQuery}
            placeholder="Search for area, street…"
            placeholderTextColor={colors.inkSubtle}
            autoFocus
            returnKeyType="search"
          />
          {query ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Clear search"
              onPress={() => setQuery('')}
              hitSlop={10}
            >
              <Icon
                name="plus"
                size={18}
                color={colors.inkMuted}
                style={styles.clear}
              />
            </Pressable>
          ) : null}
        </View>

        <ScrollView keyboardShouldPersistTaps="handled" style={styles.flex}>
          {/*
            Four states, drawn apart on purpose.

            `idle` is a prompt, not an empty result - nothing has been asked
            yet. `error` is the one that used to be indistinguishable from
            `ready` with no rows, and the two say opposite things: one is "no
            such place", the other is "we never reached the server". Only the
            second is worth a Retry, and only the second is not the customer's
            fault.
          */}
          {search.status === 'idle' ? (
            <Text variant="bodySm" style={styles.empty}>
              Type at least {MIN_QUERY_LENGTH} characters to search.
            </Text>
          ) : search.status === 'loading' ? (
            <Text variant="bodySm" style={styles.empty}>
              Searching…
            </Text>
          ) : search.status === 'error' ? (
            <View style={styles.searchError}>
              <Text variant="bodySm" style={styles.empty}>
                Couldn’t search right now.
              </Text>
              <Pressable
                accessibilityRole="button"
                onPress={() =>
                  /* Back to idle so the effect above stops seeing an answered
                     query and runs the same search again. */
                  setSearch({ for: '', status: 'idle', results: [] })
                }
                hitSlop={10}
              >
                <Text variant="labelSm" color={colors.primary}>
                  Retry
                </Text>
              </Pressable>
            </View>
          ) : search.results.length === 0 ? (
            <Text variant="bodySm" style={styles.empty}>
              Nothing matched “{search.for}”.
            </Text>
          ) : (
            search.results.map(result => (
              <PlaceRow
                key={result.id}
                place={result}
                onPress={() => chooseResult(result)}
              />
            ))
          )}

          {/*
            "Use current location" used to sit here and has moved UP, to the
            cart's address sheet.

            It was the GPS escape hatch on a screen that opened first, which
            made it the second thing a customer saw rather than one of the two
            things they chose between. In the sheet it sits beside "Add
            Address", so picking GPS or typing is one decision made before any
            screen opens - and this screen is no longer where that decision
            happens, so a second copy here would be two doors to one room.

            `ensurePermission` is no longer called from a row: the `fetching`
            effect raises the dialog itself now, which is what makes the sheet
            able to jump straight to it.
          */}
        </ScrollView>
      </SafeAreaView>
    );
  }

  // ----------------------------------------------------------------- saved
  /*
   * Unreachable in practice, and an assertion rather than a `?.` on purpose.
   *
   * `saved` is only ever entered from `found`, which cannot be reached without
   * a real fix or a searched result having set `place`. If that ever stops
   * being true, the bug is upstream in the stage machine - and silently saving
   * a half-built address is exactly the failure this whole change is removing.
   */
  if (!place) {
    return null;
  }

  const address = buildAddress();
  if (!address) {
    return null;
  }

  return (
    <View style={styles.root}>
      {/*
        The drawn backdrop, full bleed behind everything.

        Explicit width and height rather than insets alone: an `Image` given
        only insets can fall back to its intrinsic size, so the artwork would
        land anchored top-left at 1080x1920 instead of covering the screen.

        The illustration is already almost the page colour, so nothing is laid
        over it to hold the copy - a scrim or a card would only hide the one
        thing it is there to show.
      */}
      <Image
        source={require('../../assets/uploads/location/saved_bg.png')}
        style={[StyleSheet.absoluteFill, { width, height }]}
        resizeMode="cover"
      />

      <SafeAreaView style={styles.flex} edges={['top', 'bottom']}>
        <Animated.View style={[styles.centre, fade]}>
          {/* Ring, then tick. Two circles rather than one thick border: the
              outer is a halo the mark sits inside, which reads as a stamp of
              approval rather than as an outlined button. */}
          <View style={styles.savedHalo}>
            <View style={styles.savedTick}>
              <Icon
                name="check"
                size={34}
                color={colors.onPrimary}
                strokeWidth={2.6}
              />
            </View>
          </View>

          <Text variant="titleLg" align="center" style={styles.savedTitle}>
            Address Saved!
          </Text>

          <Text variant="bodySm" align="center" color={colors.inkMuted}>
            {formatAddress(address)}
          </Text>

          <Text variant="caption" align="center" style={styles.savedLabel}>
            saved as {addressName(address)}
          </Text>
        </Animated.View>

        <View style={[styles.footer, styles.footerPlain]}>
          <Button label="Done" onPress={() => onDone(address)} />
        </View>
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  flex: {
    flex: 1,
  },
  dark: {
    flex: 1,
    backgroundColor: colors.surfaceInverse,
  },
  darkBody: {
    flex: 1,
    /* `space-between` and not `flex-end`: the lockup is now the first child
       and the card the last, so this pins one to each end and lets the
       drawing fill everything between them. With `flex-end` the two would
       have stacked together at the bottom. */
    justifyContent: 'space-between',
    paddingHorizontal: screenPadding,
    paddingBottom: spacing.xl,
  },
  /* Down from the top edge, not against it - see the note at the render. */
  brandWrap: {
    alignItems: 'center',
    paddingTop: spacing.xxxl,
  },
  /*
   * Sized in points rather than as a share of the screen.
   *
   * `logo_transparent.png` is 215px wide, so past roughly 100pt a 3x screen
   * is upscaling it and the wordmark softens - the same ceiling `SplashScreen`
   * documents on its own lockup. 140 keeps it comfortably inside that on the
   * phones this ships to; a larger mark needs a higher-resolution file first.
   */
  brand: {
    /*
     * 100, down from 140. The file is 215px wide, so at 140pt a 3x screen
     * was drawing it at nearly double its own pixels and the wordmark went
     * visibly soft against the crisp line drawing behind it. 100pt is the
     * ceiling the note above already named - at or under it the upscale is
     * mild enough not to read as blur. Bigger needs a sharper asset first.
     */
    width: 100,
    /* 215x135 is the file's real size, so the box is its own 1.593 aspect.
       A box of a different shape would not distort the mark - `contain`
       letterboxes it - but it would reserve dead space around it and make
       the gap below the lockup read as larger than it is. */
    height: 63,
    /*
     * Recoloured white, because the file is not.
     *
     * `logo_transparent.png` has a real alpha channel but the mark inside it
     * is the brand's dark green - the colour that works on a pale surface and
     * all but disappears on this one. On the dark artwork it read as a faint
     * shape rather than a wordmark.
     *
     * `tintColor` repaints every opaque pixel while leaving the transparency
     * alone, so the drawing still shows through around the petals and the
     * letterforms. That is why it works here and would not if the asset had
     * a baked-in background - see the note at the render on why the Pro
     * lockup was not used.
     *
     * The alternative is a second, light-coloured logo file. This needs no
     * new asset and cannot drift out of step with the dark one.
     */
    tintColor: colors.onPrimary,
  },
  centre: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: screenPadding,
    /*
     * Nudged below the optical centre.
     *
     * Centring on the true middle of a screen that has a button pinned at its
     * foot leaves the block looking high - the eye measures the gap to the
     * button, not to the bottom edge. Padding the top pushes the whole group
     * down without moving the button.
     */
    paddingTop: spacing.xxxl * 2,
  },

  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    height: 52,
    paddingHorizontal: screenPadding,
  },
  headerSpacer: {
    width: 22,
  },

  // ------------------------------------------------------------ permission
  /* `maxWidth` so the card stays a card on a tablet rather than stretching
     into a band of surface across the artwork. */
  permissionCard: {
    width: '100%',
    maxWidth: MAX_CONTENT_WIDTH,
    alignSelf: 'center',
    backgroundColor: colors.surface,
    borderRadius: radii.xl,
    padding: spacing.xl,
    alignItems: 'center',
    ...shadows.md,
  },
  permissionMark: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: colors.surfaceAccent,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  permissionCopy: {
    marginTop: spacing.sm,
  },
  permissionCta: {
    marginTop: spacing.xl,
  },
  textButton: {
    paddingVertical: spacing.md,
  },

  // -------------------------------------------------------------- fetching
  radarWrap: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  radar: {
    opacity: 0.95,
  },
  ping: {
    position: 'absolute',
    borderWidth: 2,
    borderColor: colors.primary,
  },
  fetchTitle: {
    marginTop: spacing.xl,
    marginBottom: spacing.xs,
  },

  // ----------------------------------------------------------------- found
  /* Spans the map above the sheet and centres inside it, so the pin tracks the
     middle of what can be seen rather than the middle of the screen. */
  pinLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  /*
   * The pin's head.
   *
   * Lifted by its own height plus the stem, so the STEM'S TIP lands on the
   * centre coordinate rather than the middle of the disc. Get this wrong and
   * every address is off by whatever the offset is - invisible on screen,
   * wrong in the booking.
   */
  pinPuck: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
    borderWidth: 3,
    borderColor: colors.onPrimary,
    transform: [{ translateY: -26 }],
    ...shadows.md,
  },
  /* Points at the exact pixel the puck stands for. White-edged for the same
     reason the puck is: a bare green needle vanishes on a dark tile. */
  pinStem: {
    position: 'absolute',
    top: '50%',
    width: 4,
    height: 12,
    marginTop: -12,
    borderRadius: 2,
    backgroundColor: colors.primary,
    borderWidth: 1,
    borderColor: colors.onPrimary,
  },
  recentreLayer: {
    position: 'absolute',
    right: screenPadding,
    alignItems: 'flex-end',
  },
  recentre: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
    ...shadows.sm,
  },
  /* Capped so a long remedy sentence wraps into a card rather than stretching
     across the whole map. Right-aligned under the button it belongs to. */
  locateError: {
    marginTop: spacing.sm,
    maxWidth: 260,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radii.md,
    backgroundColor: colors.dangerSurface,
    ...shadows.sm,
  },
  /* The back disc and the search pill, side by side over the map. */
  floatRow: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
  },
  floatBack: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
    ...shadows.sm,
  },
  /* The same disc, pinned to the corner of a stage with no map under it.
     `top` is set at the render, off the safe-area inset. */
  stageBack: {
    position: 'absolute',
    left: screenPadding,
  },
  /* In the row the pill takes what the disc leaves, instead of the page. */
  floatTitleInRow: {
    flex: 1,
    alignSelf: 'auto',
    marginHorizontal: 0,
  },
  floatHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    alignItems: 'center',
    paddingTop: spacing.sm,
  },
  /*
   * The search pill on the map, now the width of the page rather than of its
   * own label.
   *
   * `floatHeader` centres its children, so this hugged the words "Select
   * Location" - a short lozenge floating mid-screen. That was survivable when
   * the flow opened on a search box and this was a secondary way back to it;
   * now that adding an address opens straight onto the map, this IS the search
   * field, and a search field the width of its placeholder does not read as
   * one. Stretching it to the page gutter makes the tap target the whole strip
   * and matches the search field on the screen it opens.
   */
  floatTitle: {
    alignSelf: 'stretch',
    marginHorizontal: screenPadding,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderRadius: radii.pill,
    backgroundColor: colors.surface,
    ...shadows.sm,
  },
  /*
   * The details, over the bottom of the map.
   *
   * Capped at 62%: the sheet carries a form, and left to size itself it would
   * cover the map it is describing. Rounded only at the top, because the bottom
   * edge is the screen's.
   */
  sheet: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    /* A little taller than the 62% it was: the panel now carries the system
       bar's inset as padding, and that comes out of this cap - so the form
       lost a row's worth of visible height when the padding was added. */
    maxHeight: '68%',
    paddingHorizontal: screenPadding,
    /* `paddingBottom` is set inline - it varies with the keyboard and the
       device's bottom inset. See the call site. */
    borderTopLeftRadius: radii.xl,
    borderTopRightRadius: radii.xl,
    backgroundColor: colors.surface,
    ...shadows.md,
  },
  grabber: {
    alignSelf: 'center',
    width: 42,
    height: 4,
    borderRadius: 2,
    marginTop: spacing.sm,
    marginBottom: spacing.md,
    backgroundColor: colors.border,
  },
  foundRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginTop: spacing.sm,
  },
  panelCta: {
    marginTop: spacing.lg,
  },
  /* Sits directly above the disabled button, so the reason and the thing it
     stops are read together. */
  resolveNote: {
    marginTop: spacing.md,
    paddingHorizontal: spacing.xs,
  },

  // ---------------------------------------------------------------- search
  searchField: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginHorizontal: screenPadding,
    paddingHorizontal: spacing.md,
    height: 46,
    borderRadius: radii.md,
    backgroundColor: colors.surface,
    ...hairline,
  },
  searchInput: {
    flex: 1,
    ...inputText,
  },
  /* The icon set has no cross; a rotated plus is the same shape. */
  clear: {
    transform: [{ rotate: '45deg' }],
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingHorizontal: screenPadding,
    paddingVertical: spacing.md,
    borderBottomWidth: hairline.borderWidth,
    borderBottomColor: hairline.borderColor,
  },
  rowText: {
    flex: 1,
  },
  locateRow: {
    /* Kept visually apart from the results above it: it is a different kind
       of thing - an action, not one more address to choose from. */
    borderTopWidth: hairline.borderWidth,
    borderTopColor: colors.border,
  },
  coverage: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.xs,
    marginTop: 2,
  },
  coverageText: {
    flex: 1,
  },
  searchError: {
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.md,
  },
  empty: {
    padding: screenPadding,
  },

  // ---------------------------------------------------- the details form
  /*
   * The scrolling half of the panel. The save button sits outside it, so it
   * stays put while the fields move under it.
   */
  /*
   * `flexShrink`, NOT `flex: 1` - and the difference is whether "Save Address"
   * exists on a tall phone.
   *
   * ---------------------------------------------------------------------------
   * WHAT WENT WRONG ON SOME HANDSETS AND NOT OTHERS
   * ---------------------------------------------------------------------------
   * The panel above has no height. It has `maxHeight: '68%'` and a `bottom`,
   * so it sizes to its own content and is capped there. `flex: 1` inside a box
   * with no definite height is the fragile shape: it only lands correctly while
   * Yoga is measuring the panel against that cap, and what it produces when the
   * content wants more is a scroll view stretched to its full content with the
   * button pushed out past the cap - clipped away by the panel's own rounded
   * corners, with nothing left to scroll.
   *
   * `flexShrink: 1` does not need the cap to be reached to be correct. When the
   * content fits, the panel hugs it. When it does not, the scroll view is the
   * only child that may give up space (RN defaults `flexShrink` to 0, so the
   * grabber, the note and the button all keep their natural height) - so it
   * gets a bounded viewport, which is the one condition under which a
   * `ScrollView` scrolls at all.
   *
   * ---------------------------------------------------------------------------
   * WHY IT WAS PHONE-DEPENDENT, WHICH IS THE PART WORTH REMEMBERING
   * ---------------------------------------------------------------------------
   * Nothing here is different per device. The CONTENT is:
   *
   *  - the `roughFix` warning is three extra lines, and it appears only when
   *    that handset's fix is poor - the same screen, a different height, on a
   *    phone with a worse GPS;
   *  - the out-of-area notice and `resolveNote` come and go the same way;
   *  - `paddingBottom` carries `Math.max(insets.bottom, spacing.xxl)`, so a
   *    three-button navigation bar eats ~24dp more of the cap than gestures do;
   *  - Android's font-size and display-size settings scale all of it.
   *
   * So it fitted inside 68% on the phones it was built on and did not on
   * others, and only the ones where it did not ever showed the bug.
   */
  panelInner: {
    flexShrink: 1,
  },
  panelScroll: {
    flexShrink: 1,
  },
  panelBody: {
    gap: spacing.lg,
    paddingBottom: spacing.md,
  },
  field: {
    gap: spacing.sm,
  },
  fieldLabel: {
    marginBottom: 2,
  },
  chips: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  chip: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderRadius: radii.pill,
    backgroundColor: colors.surface,
    ...hairline,
  },
  chipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  addressBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surface,
    ...hairline,
  },
  nameBox: {
    height: 46,
    marginTop: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surface,
    ...hairline,
    ...inputText,
  },
  /* The token's `padding: 0` is overridden on purpose - a multi-line box needs
     its text held off the border, unlike the single-line fields. */
  noteBox: {
    minHeight: 68,
    borderRadius: radii.md,
    backgroundColor: colors.surface,
    textAlignVertical: 'top',
    ...hairline,
    ...inputText,
    padding: spacing.md,
  },
  defaultRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },

  footer: {
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  footerPlain: {
    backgroundColor: 'transparent',
    borderTopWidth: 0,
    paddingBottom: spacing.xl,
  },

  // ----------------------------------------------------------------- saved
  /* The halo, in the pale wash so it reads against the drawn background
     without becoming a second solid shape next to the tick. */
  savedHalo: {
    width: 116,
    height: 116,
    borderRadius: 58,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
    marginBottom: spacing.xl,
  },
  savedTick: {
    width: 80,
    height: 80,
    borderRadius: 40,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
  },
  savedTitle: {
    marginBottom: spacing.sm,
  },
  savedLabel: {
    marginTop: spacing.xs,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default LocationScreen;
